import React, { useEffect, useState, useMemo, useCallback } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import BootstrapTable from 'react-bootstrap-table-next';
import paginationFactory from 'react-bootstrap-table2-paginator';
import { Button, ButtonGroup, Spinner } from 'react-bootstrap';
import { readCookie } from '../../cookie';
import { getAllApiariesService, getAllHiveActivityLogs } from '../../../../services/apiaryService';

// Import Modular Components
import HouseholdSummaryCards from './components/HouseholdSummaryCards';
import HouseholdFiltersBar from './components/HouseholdFiltersBar';
import HouseholdMapComponent from './components/HouseholdMapComponent';
import {
    getHouseholdColumns,
    getLeadVillage,
    getLeadBlock,
    getLeadDistrict,
    getLeadState,
    getLeadAadhaar,
    getLeadSrlm
} from './components/HouseholdColumns';

import ExportCSVModal from '../../2-leads/ExportCSVModal';
import LeadQuickViewDrawer from '../../2-leads/LeadQuickViewDrawer';

// Helper to clean raw visitor name and replace literal "Self" with candidate's actual name
const cleanVisitorName = (rawName, candidateName, surveyorName) => {
    if (!rawName || rawName === 'null' || rawName === 'undefined') {
        return candidateName || surveyorName || '-';
    }

    let str = rawName.toString().trim();

    // Handle "Self (Name - Mobile)" -> Extract "Name"
    if (str.startsWith('Self (') || str.startsWith('Self(')) {
        const inner = str.replace(/^Self\s*\(/i, '').replace(/\)$/, '').trim();
        const parts = inner.split('-');
        if (parts[0] && parts[0].trim()) {
            return parts[0].trim();
        }
        return candidateName || 'Beekeeper';
    }

    // Handle exact "Self" -> Return candidateName!
    if (str.toLowerCase() === 'self') {
        return candidateName || 'Beekeeper';
    }

    // Handle "Bee-mitra (Name - Mobile)" -> Extract "Name"
    if (str.includes('(') && str.includes(')')) {
        const match = str.match(/\(([^)]+)\)/);
        if (match && match[1]) {
            const innerParts = match[1].split('-');
            if (innerParts[0] && innerParts[0].trim()) {
                const prefix = str.split('(')[0].trim();
                return `${prefix ? prefix + ' ' : ''}${innerParts[0].trim()}`;
            }
        }
    }

    return str;
};

// Helper to safely parse MongoDB $date, ISO strings, or nested date objects
const parseLogTimestamp = (dateVal) => {
    if (!dateVal) return 0;
    if (typeof dateVal === 'number') return dateVal;
    if (typeof dateVal === 'string') {
        const t = new Date(dateVal).getTime();
        return isNaN(t) ? 0 : t;
    }
    if (typeof dateVal === 'object') {
        if (dateVal.$date) {
            const t = new Date(dateVal.$date).getTime();
            return isNaN(t) ? 0 : t;
        }
        if (dateVal.date) {
            const t = new Date(dateVal.date).getTime();
            return isNaN(t) ? 0 : t;
        }
    }
    return 0;
};

// Styled Components
const MainWrapper = styled.div`
    padding: ${props => props.isEmbedded ? '20px 0' : '24px'};
    margin-left: ${props => props.isEmbedded ? '0' : '15%'};
    width: ${props => props.isEmbedded ? '100%' : '85%'};
    background: #f8fafc;
    min-height: 100vh;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
    box-sizing: border-box;

    @media (max-width: 992px) {
        margin-left: 0;
        width: 100%;
    }
`;

const HeaderContainer = styled.div`
    margin-bottom: 20px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 15px;
`;

const TitleGroup = styled.div`
    h3 {
        margin: 0;
        font-weight: 800;
        color: #0f172a;
        font-size: 24px;
        letter-spacing: -0.5px;
    }
    p {
        margin: 4px 0 0 0;
        color: #64748b;
        font-size: 14px;
    }
`;

const TableWrapper = styled.div`
    background: #fff;
    border-radius: 14px;
    padding: 20px;
    box-shadow: 0 4px 20px rgba(0,0,0,0.03);
    border: 1px solid #e2e8f0;
    overflow-x: auto;

    .react-bootstrap-table {
        border: none;
    }

    th {
        background: #f8fafc;
        border: none !important;
        border-bottom: 2px solid #e2e8f0 !important;
        color: #475569;
        font-size: 12px;
        font-weight: 800;
        text-transform: uppercase;
        padding: 12px 8px !important;
        letter-spacing: 0.5px;
        white-space: nowrap;
    }

    td {
        border-color: #f1f5f9 !important;
        vertical-align: middle !important;
        padding: 10px 8px !important;
        font-size: 13px;
        color: #334155;
        white-space: normal !important;
        word-break: break-word;
    }

    tr:hover {
        background-color: #f8fafc;
    }
`;

export default function HouseholdApiariesView({ isEmbedded = false }) {
    const [loading, setLoading] = useState(true);
    const [activeHouseholdLeads, setActiveHouseholdLeads] = useState([]);
    const [viewMode, setViewMode] = useState('list');
    const [showExportModal, setShowExportModal] = useState(false);

    // Quick View Drawer State
    const [quickViewVisible, setQuickViewVisible] = useState(false);
    const [quickViewLeadId, setQuickViewLeadId] = useState(null);

    // Cascading Filter States
    const [filters, setFilters] = useState({
        search: '',
        stage: '',
        gender: '',
        state: '',
        district: '',
        block: '',
        village: '',
        cluster: '',
        lastVisitedBy: '',
        srlm: ''
    });

    const [statesList, setStatesList] = useState([]);
    const [districtsList, setDistrictsList] = useState([]);
    const [blocksList, setBlocksList] = useState([]);
    const [villagesList, setVillagesList] = useState([]);
    const [clustersList, setClustersList] = useState([]);
    const [lastVisitedByList, setLastVisitedByList] = useState([]);

    const fetchAllData = useCallback(async () => {
        try {
            setLoading(true);
            const currentToken = JSON.parse(readCookie('access_token') || 'null');
            const APP_USER_URL = process.env.REACT_APP_API_APP_USER_URL || 'https://devapi.thehumblebee.co/nector/app';
            const APP_FIELD_URL = process.env.REACT_APP_API_APP_FIELD_URL || 'https://devapi.thehumblebee.co/nector/app';
            const HUMBLEBEE_API_URL = process.env.REACT_APP_HUMBLEBEE_API_URL || 'https://devapi.thehumblebee.co/nector/app';

            const [surveyedUsersRes, baseProfilesRes, clustersRes, apiariesRes, activityLogsRes] = await Promise.allSettled([
                axios.get(`${APP_USER_URL}/user/getAllSurveyedUsers?limit=2500&role=trainee_beekeeper`, {
                    headers: { source: 'humblebee_admin', Authorization: 'Bearer ' + currentToken }
                }),
                axios.get(`${APP_FIELD_URL}/surveys/bp/admin-base-profile`, {
                    headers: { source: 'humblebee_admin', Authorization: 'Bearer ' + currentToken }
                }),
                axios.get(`${HUMBLEBEE_API_URL}/projects/clusters/getAll`, {
                    headers: { source: 'humblebee_admin', Authorization: 'Bearer ' + currentToken }
                }),
                getAllApiariesService(),
                getAllHiveActivityLogs()
            ]);

            const rawSurveyed = surveyedUsersRes.status === 'fulfilled' ? surveyedUsersRes.value?.data : null;
            const surveyedUsers = Array.isArray(rawSurveyed)
                ? rawSurveyed
                : (Array.isArray(rawSurveyed?.userInfo) ? rawSurveyed.userInfo : (Array.isArray(rawSurveyed?.data) ? rawSurveyed.data : []));

            const basicProfiles = baseProfilesRes.status === 'fulfilled' && baseProfilesRes.value?.data?.data ? baseProfilesRes.value.data.data : [];
            const clustersData = clustersRes.status === 'fulfilled' && Array.isArray(clustersRes.value?.data?.clusters) ? clustersRes.value.data.clusters : [];

            // Process Global Hive Activity Logs
            const activityLogs = activityLogsRes.status === 'fulfilled' && Array.isArray(activityLogsRes.value) ? activityLogsRes.value : [];
            const activityMap = {};

            activityLogs.forEach(log => {
                if (log.isDeleted) return;

                const mKeys = [
                    log.mobile_number,
                    log.mobile,
                    log.phone,
                    log.beekeeper_mobile,
                    log.apiary?.mobile_number
                ].filter(Boolean).map(m => m.toString().replace(/\D/g, '').slice(-10));

                const idKeys = [
                    log.user_id,
                    log.beekeeper_id,
                    log.candidate_id,
                    log.apiary_id?.$oid || log.apiary_id,
                    log.apiary?._id,
                    log.created_by?.$oid || log.created_by,
                    log.updated_by?.$oid || log.updated_by,
                    log.le_app_user
                ].filter(Boolean).map(id => id.toString());

                const allKeys = [...mKeys, ...idKeys];
                allKeys.forEach(k => {
                    if (!activityMap[k]) activityMap[k] = [];
                    activityMap[k].push(log);
                });
            });

            // Build Apiary Map
            const apiaryMap = {};
            const userApiaryDataMap = {};
            const candidateApiariesMap = {};
            const apiariesList = apiariesRes.status === 'fulfilled' ? (apiariesRes.value?.data || apiariesRes.value) : [];

            if (Array.isArray(apiariesList)) {
                apiariesList.forEach(apiary => {
                    const mobileKeys = [
                        apiary.mobile_number,
                        apiary.mobile,
                        apiary.beekeeper_mobile,
                        apiary.phone
                    ].filter(Boolean).map(m => m.toString().replace(/\D/g, '').slice(-10));

                    const idKeys = [
                        apiary.beekeeper_id,
                        apiary.user_id,
                        apiary.candidate_id,
                        apiary._id
                    ].filter(Boolean).map(id => (typeof id === 'object' && id?.$oid ? id.$oid : id.toString()));

                    const isApiaryActive = apiary.is_active !== false && apiary.operational_status !== 'Disabled' && apiary.status !== 'INACTIVE';
                    const statusVal = isApiaryActive ? 'Active' : 'Inactive';

                    mobileKeys.forEach(key => { if (key) apiaryMap[key] = statusVal; });
                    idKeys.forEach(key => { if (key) apiaryMap[key] = statusVal; });

                    [...mobileKeys, ...idKeys].forEach(k => {
                        if (k) {
                            if (!candidateApiariesMap[k]) candidateApiariesMap[k] = [];
                            candidateApiariesMap[k].push(apiary);
                        }
                    });
                });
            }

            const combined = [];
            const seenUserIds = new Set();
            const seenMobiles = new Set();

            // 1. Process Advanced Surveyed Users
            if (surveyedUsers && surveyedUsers.length) {
                surveyedUsers.forEach((item, idx) => {
                    const userId = item.userInfo?._id || item._id || item.id;
                    const mobile = item.userInfo?.mobileNumber
                        ? item.userInfo.mobileNumber.toString().replace(/\D/g, '').slice(-10)
                        : '';

                    const isDuplicateUser = userId && seenUserIds.has(userId.toString());
                    const isDuplicateMobile = mobile && mobile !== '0000000000' && seenMobiles.has(mobile);

                    if (isDuplicateUser || isDuplicateMobile) return;

                    if (userId) seenUserIds.add(userId.toString());
                    if (mobile && mobile !== '0000000000') seenMobiles.add(mobile);

                    const uniqueKey = userId ? userId.toString() : `advance_${idx}`;
                    combined.push({
                        ...item,
                        stage: 'advance',
                        _id: uniqueKey,
                        id: uniqueKey,
                        name: item.userInfo?.name || item.name || 'N/A',
                        mobileNumber: item.userInfo?.mobileNumber || item.mobile_number || 'N/A',
                        clusterName: item.clusterName || item.userInfo?.cluster || item.fieldViewData?.[0]?.cluster_name || null,
                    });
                });
            }

            // 2. Process Basic Profiles
            if (basicProfiles && basicProfiles.length) {
                basicProfiles.forEach((profile, idx) => {
                    const userId = profile._id || profile.id;
                    const mobile = profile.mobile_number
                        ? profile.mobile_number.toString().replace(/\D/g, '').slice(-10)
                        : '';

                    const isDuplicateUser = userId && seenUserIds.has(userId.toString());
                    const isDuplicateMobile = mobile && mobile !== '0000000000' && seenMobiles.has(mobile);

                    if (isDuplicateUser || isDuplicateMobile) return;

                    if (userId) seenUserIds.add(userId.toString());
                    if (mobile && mobile !== '0000000000') seenMobiles.add(mobile);

                    const uniqueKey = userId ? userId.toString() : `basic_${idx}`;
                    combined.push({
                        ...profile,
                        stage: 'basic',
                        _id: uniqueKey,
                        id: uniqueKey,
                        userInfo: {
                            _id: profile._id,
                            name: profile.name || profile.full_name,
                            mobileNumber: profile.mobile_number,
                            gender: profile.gender,
                            address: {
                                state: profile.state,
                                district: profile.district,
                                village: profile.village_name || profile.village,
                                block: profile.block,
                            }
                        },
                        name: profile.name || profile.full_name,
                        mobileNumber: profile.mobile_number,
                        clusterName: profile.clusterName || profile.cluster_name || null,
                    });
                });
            }

            // Fetch per-user survey apiaries in parallel to extract true candidate activities & visits
            const userSurveyApiaryResults = await Promise.allSettled(
                combined.map(async (item) => {
                    const userId = item.userInfo?._id || item._id || item.id;
                    if (!userId) return null;
                    try {
                        const res = await axios.get(
                            `${APP_FIELD_URL}/surveys/user/${userId}`,
                            {
                                headers: {
                                    Authorization: 'Bearer ' + currentToken,
                                    source: 'humblebee_admin',
                                },
                            }
                        );
                        const apiaries = res.data?.apiary;
                        if (Array.isArray(apiaries) && apiaries.length > 0) {
                            return { userId: userId.toString(), hasApiary: true, apiaries };
                        }
                    } catch (e) {
                        // ignore error
                    }
                    return null;
                })
            );

            userSurveyApiaryResults.forEach(res => {
                if (res.status === 'fulfilled' && res.value && res.value.hasApiary) {
                    const { userId, apiaries } = res.value;
                    const isApiaryActive = apiaries.some(a => a.is_active !== false && a.operational_status !== 'Disabled' && a.status !== 'INACTIVE');
                    const statusVal = isApiaryActive ? 'Active' : 'Inactive';
                    apiaryMap[userId] = statusVal;
                    userApiaryDataMap[userId] = apiaries;
                }
            });

            // Resolve Cluster, Project Names, True Verified Apiary Status, and Exact Visit Timeline
            const combinedData = combined.map(item => {
                const itemUserId = item.userInfo?._id || item._id || item.id;
                const candidateName = item.userInfo?.name || item.name || 'N/A';
                const surveyorName = item.surveyData?.created_by?.name || item.created_by_user?.name || item.surveyorName || item.surveyedBy || item.fieldViewData?.[0]?.surveyor_name || item.userInfo?.surveyedBy || null;

                const itemMobile = item.userInfo?.mobileNumber
                    ? item.userInfo.mobileNumber.toString().replace(/\D/g, '').slice(-10)
                    : (item.mobile_number ? item.mobile_number.toString().replace(/\D/g, '').slice(-10) : '');
                const itemIdStr = itemUserId ? itemUserId.toString() : '';
                const fieldViewIdStr = item.fieldViewData?.[0]?._id ? item.fieldViewData[0]._id.toString() : '';

                const matchedCluster = clustersData.find(c => {
                    if (c.trainee_ids && Array.isArray(c.trainee_ids)) {
                        return c.trainee_ids.some(m => {
                            const mUserId = m.user_id ? m.user_id.toString() : null;
                            const mId = m._id ? m._id.toString() : null;
                            const mMobile = m.mobile_number ? m.mobile_number.toString().replace(/\D/g, '').slice(-10) : null;
                            return (itemUserId && (mUserId === itemUserId || mId === itemUserId)) || (itemMobile && mMobile === itemMobile);
                        });
                    }
                    return false;
                });

                const leadDist = getLeadDistrict(item);
                let resolvedCluster = item.clusterName ||
                    matchedCluster?.clusterName ||
                    item.fieldViewData?.[0]?.cluster_name ||
                    item.surveyData?.stages?.[0]?.form_data?.cluster_name;

                if (!resolvedCluster || resolvedCluster === 'N/A' || resolvedCluster === 'null') {
                    if (leadDist && leadDist !== '-') resolvedCluster = `${leadDist} Cluster`;
                    else resolvedCluster = 'N/A';
                }

                // Exact Apiary Status Resolution
                const apiaryStatus = apiaryMap[itemMobile] || apiaryMap[itemIdStr] || apiaryMap[fieldViewIdStr] || 'Absent';

                // Unified Activity Timeline Calculation across all candidate's apiaries & logs
                const userApiaryList = userApiaryDataMap[itemIdStr] || [];
                const matchedApiaries = [
                    ...(candidateApiariesMap[itemMobile] || []),
                    ...(candidateApiariesMap[itemIdStr] || []),
                    ...(candidateApiariesMap[fieldViewIdStr] || [])
                ];

                const allCandidateApiaries = [...userApiaryList, ...matchedApiaries];
                const apiaryObj = allCandidateApiaries[0] || {};

                const allCandidateApiaryIds = new Set();
                allCandidateApiaries.forEach(a => {
                    const aId = a._id?.$oid || a._id || a.apiary_id?.$oid || a.apiary_id;
                    if (aId) allCandidateApiaryIds.add(aId.toString());
                });
                if (item.apiary_id) {
                    const aId = item.apiary_id?.$oid || item.apiary_id;
                    allCandidateApiaryIds.add(aId.toString());
                }
                if (item.surveyData?.apiary_id) {
                    const aId = item.surveyData.apiary_id?.$oid || item.surveyData.apiary_id;
                    allCandidateApiaryIds.add(aId.toString());
                }

                const searchKeys = [itemMobile, itemIdStr, fieldViewIdStr, ...allCandidateApiaryIds].filter(Boolean);
                const userLogs = [];
                const seenLogIds = new Set();
                searchKeys.forEach(k => {
                    (activityMap[k] || []).forEach(log => {
                        const logId = log._id?.$oid || log._id || `${log.created_at}_${log.visited_by_name || log.userName?.name}`;
                        if (!seenLogIds.has(logId)) {
                            seenLogIds.add(logId);
                            userLogs.push(log);
                        }
                    });
                });

                const surveyActivities = [];
                const surveyInspections = [];
                allCandidateApiaries.forEach(ap => {
                    const acts = Array.isArray(ap.activities) ? ap.activities : (Array.isArray(ap.activity) ? ap.activity : []);
                    const insps = Array.isArray(ap.inspections) ? ap.inspections : (Array.isArray(ap.inspection_reports) ? ap.inspection_reports : []);
                    acts.forEach(a => surveyActivities.push(a));
                    insps.forEach(i => surveyInspections.push(i));
                });

                const timeline = [];

                userLogs.forEach(l => {
                    const rawName = l.visited_by_name || l.visited_by?.name || l.createdBy?.name || l.userName?.name || l.logged_by || l.user_name || l.surveyor_name;
                    const logUserId = l.visited_by_id?.$oid || l.visited_by_id || l.visited_by?._id || l.createdBy?._id || l.created_by?.$oid || l.created_by || l.user_id;
                    const dateVal = l.updated_at?.$date || l.updated_at || l.created_at?.$date || l.created_at || l.createdAt || l.date;

                    timeline.push({
                        timestamp: parseLogTimestamp(dateVal),
                        rawName,
                        logUserId
                    });
                });

                surveyActivities.forEach(act => {
                    const rawName = act.visited_by_name || act.visited_by?.name || act.logged_by || act.loggedBy || act.createdBy?.name || act.userName?.name || act.user_name;
                    const logUserId = act.visited_by_id?.$oid || act.visited_by_id || act.visited_by?._id || act.logged_by_id || act.created_by?.$oid || act.created_by || act.user_id;
                    const dateVal = act.updated_at?.$date || act.updated_at || act.created_at?.$date || act.created_at || act.createdAt || act.date;

                    timeline.push({
                        timestamp: parseLogTimestamp(dateVal),
                        rawName,
                        logUserId
                    });
                });

                surveyInspections.forEach(insp => {
                    const rawName = insp.visited_by_name || insp.visited_by?.name || insp.logged_by || insp.loggedBy || insp.inspector_name || insp.user_name;
                    const logUserId = insp.visited_by_id?.$oid || insp.visited_by_id || insp.visited_by?._id || insp.logged_by_id || insp.created_by?.$oid || insp.created_by || insp.user_id;
                    const dateVal = insp.updated_at?.$date || insp.updated_at || insp.created_at?.$date || insp.created_at || insp.date_of_visit || insp.date || insp.createdAt;

                    timeline.push({
                        timestamp: parseLogTimestamp(dateVal),
                        rawName,
                        logUserId
                    });
                });

                // Sort timeline descending by timestamp
                timeline.sort((a, b) => b.timestamp - a.timestamp);

                const visitCount = timeline.length;

                let lastVisitedOn = '-';
                let lastVisitedBy = cleanVisitorName(null, candidateName, surveyorName);
                let lastVisitedById = itemUserId;

                if (timeline.length > 0) {
                    const latest = timeline[0];
                    if (latest.timestamp > 0) {
                        lastVisitedOn = new Date(latest.timestamp).toLocaleDateString('en-GB', {
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric'
                        });
                    }
                    lastVisitedBy = cleanVisitorName(latest.rawName, candidateName, surveyorName);
                    if (latest.logUserId) {
                        lastVisitedById = typeof latest.logUserId === 'object' ? (latest.logUserId.$oid || latest.logUserId._id || String(latest.logUserId)) : String(latest.logUserId);
                    }
                } else if (apiaryObj.setup_date || apiaryObj.createdAt) {
                    const setupDateVal = apiaryObj.setup_date || apiaryObj.createdAt;
                    lastVisitedOn = new Date(setupDateVal).toLocaleDateString('en-GB', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric'
                    });
                    lastVisitedBy = cleanVisitorName(null, candidateName, surveyorName);
                    lastVisitedById = itemUserId;
                }

                return {
                    ...item,
                    clusterName: resolvedCluster,
                    projectName: matchedCluster?.projectId?.name || item.projectName || 'Dantewada PMDDKY - Had X NSC',
                    apiaryStatus,
                    hivesCount: Number(item.surveyData?.hive_count || item.hives_count || 10),
                    visitCount,
                    lastVisitedOn,
                    lastVisitedBy,
                    lastVisitedById
                };
            }).filter(item => item.apiaryStatus === 'Active');

            setActiveHouseholdLeads(combinedData);

            // Populate Filter Option Lists
            const states = [...new Set(combinedData.map(item => getLeadState(item)).filter(s => s && s !== '-'))].sort();
            const clusters = [...new Set(combinedData.map(item => item.clusterName).filter(Boolean))].sort();
            const visitors = [...new Set(combinedData.map(item => item.lastVisitedBy).filter(v => v && v !== '-'))].sort();

            setStatesList(states);
            setClustersList(clusters);
            setLastVisitedByList(visitors);

        } catch (err) {
            console.error("Error loading Household Apiaries data:", err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchAllData();
    }, [fetchAllData]);

    // Cascading Location Dropdowns using getLeadState/District/Block/Village
    useEffect(() => {
        let relevantLeads = activeHouseholdLeads;
        if (filters.state) {
            relevantLeads = relevantLeads.filter(item => getLeadState(item) === filters.state);
        }
        const districts = [...new Set(relevantLeads.map(item => getLeadDistrict(item)).filter(d => d && d !== '-'))].sort();
        setDistrictsList(districts);
    }, [filters.state, activeHouseholdLeads]);

    useEffect(() => {
        let relevantLeads = activeHouseholdLeads;
        if (filters.state) relevantLeads = relevantLeads.filter(item => getLeadState(item) === filters.state);
        if (filters.district) relevantLeads = relevantLeads.filter(item => getLeadDistrict(item) === filters.district);

        const blocks = [...new Set(relevantLeads.map(item => getLeadBlock(item)).filter(b => b && b !== '-'))].sort();
        setBlocksList(blocks);
    }, [filters.state, filters.district, activeHouseholdLeads]);

    useEffect(() => {
        let relevantLeads = activeHouseholdLeads;
        if (filters.state) relevantLeads = relevantLeads.filter(item => getLeadState(item) === filters.state);
        if (filters.district) relevantLeads = relevantLeads.filter(item => getLeadDistrict(item) === filters.district);
        if (filters.block) relevantLeads = relevantLeads.filter(item => getLeadBlock(item)) === filters.block;

        const villages = [...new Set(relevantLeads.map(item => getLeadVillage(item)).filter(v => v && v !== '-'))].sort();
        setVillagesList(villages);
    }, [filters.state, filters.district, filters.block, activeHouseholdLeads]);

    // Filter Logic matching /leads
    const filteredLeads = useMemo(() => {
        return activeHouseholdLeads.filter(item => {
            const name = (item.userInfo?.name || item.name || '').toLowerCase();
            const mobile = (item.userInfo?.mobileNumber || item.mobile_number || '').toString();
            const search = (filters.search || '').toLowerCase();

            const matchesSearch = !search || name.includes(search) || mobile.includes(search);
            const matchesStage = !filters.stage || (filters.stage === 'advance' ? item.stage === 'advance' : item.stage === 'basic');
            const matchesGender = !filters.gender || (item.userInfo?.gender || item.gender || '').toLowerCase() === filters.gender.toLowerCase();
            const matchesState = !filters.state || getLeadState(item) === filters.state;
            const matchesDistrict = !filters.district || getLeadDistrict(item) === filters.district;
            const matchesBlock = !filters.block || getLeadBlock(item) === filters.block;
            const matchesVillage = !filters.village || getLeadVillage(item) === filters.village;
            const matchesCluster = !filters.cluster || item.clusterName === filters.cluster;
            const matchesLastVisitedBy = !filters.lastVisitedBy || item.lastVisitedBy === filters.lastVisitedBy;
            const matchesSrlm = !filters.srlm || (filters.srlm === 'yes' ? getLeadSrlm(item) === true : getLeadSrlm(item) === false);

            return matchesSearch && matchesStage && matchesGender && matchesState && matchesDistrict && matchesBlock && matchesVillage && matchesCluster && matchesLastVisitedBy && matchesSrlm;
        });
    }, [activeHouseholdLeads, filters]);

    const stats = useMemo(() => {
        const totalHouseholdApiaries = filteredLeads.length;
        const totalHivesSum = filteredLeads.reduce((sum, item) => sum + (Number(item.hivesCount) || 0), 0);
        return { totalHouseholdApiaries, totalHivesSum, mappedLocationsCount: totalHouseholdApiaries };
    }, [filteredLeads]);

    const openQuickView = (userId) => {
        setQuickViewLeadId(userId);
        setQuickViewVisible(true);
    };

    const columns = useMemo(() => getHouseholdColumns(openQuickView), []);

    if (loading) {
        return (
            <MainWrapper isEmbedded={isEmbedded}>
                <div className="text-center p-5 mt-5">
                    <Spinner animation="border" variant="primary" size="lg" />
                    <p className="mt-3 text-muted" style={{ fontSize: '15px', fontWeight: 600 }}>
                        Fetching Household Apiaries & Map Locations...
                    </p>
                </div>
            </MainWrapper>
        );
    }

    return (
        <MainWrapper isEmbedded={isEmbedded}>
            <HeaderContainer>
                <TitleGroup>
                    <h3>🏡 Beekeeper Household Apiaries</h3>
                </TitleGroup>

                <ButtonGroup size="sm">
                    <Button
                        variant={viewMode === 'list' ? 'primary' : 'outline-primary'}
                        style={{ borderRadius: '10px 0 0 10px', padding: '10px 20px', fontWeight: 700 }}
                        onClick={() => setViewMode('list')}
                    >
                        <i className="fa fa-list mr-2"></i> List View ({filteredLeads.length})
                    </Button>
                    <Button
                        variant={viewMode === 'map' ? 'primary' : 'outline-primary'}
                        style={{ borderRadius: '0 10px 10px 0', padding: '10px 20px', fontWeight: 700 }}
                        onClick={() => setViewMode('map')}
                    >
                        <i className="fa fa-map mr-2"></i> Map View ({stats.mappedLocationsCount})
                    </Button>
                </ButtonGroup>
            </HeaderContainer>

            <HouseholdSummaryCards stats={stats} />

            <HouseholdFiltersBar
                filters={filters}
                setFilters={setFilters}
                statesList={statesList}
                districtsList={districtsList}
                blocksList={blocksList}
                villagesList={villagesList}
                clustersList={clustersList}
                lastVisitedByList={lastVisitedByList}
                onOpenExport={() => setShowExportModal(true)}
            />

            <ExportCSVModal
                show={showExportModal}
                onHide={() => setShowExportModal(false)}
                data={filteredLeads}
            />

            <LeadQuickViewDrawer
                visible={quickViewVisible}
                leadId={quickViewLeadId}
                defaultTab="apiaryDetails"
                onClose={() => {
                    setQuickViewVisible(false);
                    setQuickViewLeadId(null);
                }}
            />

            {viewMode === 'list' ? (
                <TableWrapper>
                    <BootstrapTable
                        keyField="_id"
                        data={filteredLeads}
                        columns={columns}
                        pagination={paginationFactory({
                            sizePerPageList: [20, 50, 100, 200, 500],
                            sizePerPage: 200,
                            showTotal: true,
                        })}
                        bootstrap4
                        hover
                    />
                </TableWrapper>
            ) : (
                <HouseholdMapComponent leads={filteredLeads} onSelectLead={openQuickView} />
            )}
        </MainWrapper>
    );
}
